import { ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Logo } from '@/components/ui/logo'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import {
  LayoutDashboard,
  Key,
  Settings,
  LogOut,
  Menu,
  X,
  ExternalLink,
  BookOpen,
} from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'

interface LayoutProps {
  children: ReactNode
}

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'API Keys', href: '/api-keys', icon: Key },
  { name: 'Settings', href: '/settings', icon: Settings },
]

export function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuthStore()
  const { sidebarOpen, toggleSidebar } = useUIStore()
  const location = useLocation()
  const navigate = useNavigate()

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut()
      logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  return (
    <div className='min-h-screen bg-background'>
      {/* Mobile menu button */}
      <div className='lg:hidden fixed top-4 left-4 z-50'>
        <Button variant='outline' size='sm' onClick={toggleSidebar}>
          {sidebarOpen ? <X className='w-4 h-4' /> : <Menu className='w-4 h-4' />}
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-40 w-64 bg-card border-r border-border transform transition-transform duration-200 ease-in-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className='flex flex-col h-full'>
          {/* Logo */}
          <div className='p-6'>
            <Logo size='md' />
          </div>

          <Separator />

          {/* Navigation */}
          <nav className='flex-1 p-4 space-y-2'>
            {navigation.map(item => {
              const isActive = location.pathname === item.href
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  )}
                >
                  <item.icon className='w-4 h-4' />
                  <span>{item.name}</span>
                </Link>
              )
            })}
          </nav>

          <Separator />

          {/* Documentation Link */}
          <div className='p-4'>
            <a
              href='https://docs.openartifacts.com'
              target='_blank'
              rel='noopener noreferrer'
              className='flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-accent'
            >
              <BookOpen className='w-4 h-4' />
              <span>Documentation</span>
              <ExternalLink className='w-3 h-3 ml-auto' />
            </a>
          </div>

          <Separator />

          {/* User section */}
          <div className='p-4 space-y-4'>
            <div className='flex items-center space-x-3'>
              {user?.user_metadata?.avatar_url ? (
                <img
                  src={user.user_metadata.avatar_url}
                  alt={user?.user_metadata?.user_name || 'User'}
                  className='w-8 h-8 rounded-full object-cover'
                  onError={e => {
                    // Fallback to initials if image fails to load
                    e.currentTarget.style.display = 'none'
                    e.currentTarget.nextElementSibling?.classList.remove('hidden')
                  }}
                />
              ) : null}
              <div
                className={`w-8 h-8 bg-primary rounded-full flex items-center justify-center ${
                  user?.user_metadata?.avatar_url ? 'hidden' : ''
                }`}
              >
                <span className='text-sm font-medium text-primary-foreground'>
                  {user?.user_metadata?.user_name?.[0]?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className='flex-1 min-w-0'>
                <p className='text-sm font-medium truncate'>
                  {user?.user_metadata?.user_name || 'User'}
                </p>
                <p className='text-xs text-muted-foreground truncate'>{user?.email}</p>
              </div>
            </div>

            <div className='flex items-center space-x-2'>
              <Button variant='outline' size='sm' onClick={handleLogout} className='flex-1'>
                <LogOut className='w-4 h-4 mr-2' />
                Sign Out
              </Button>
              <ThemeToggle variant='outline' size='sm' showDropdown={false} />
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div
        className={cn(
          'lg:ml-64 min-h-screen transition-all duration-200',
          sidebarOpen ? 'lg:ml-64' : 'lg:ml-64'
        )}
      >
        <main className='p-6 lg:p-8'>{children}</main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className='fixed inset-0 z-30 bg-black/50 lg:hidden' onClick={toggleSidebar} />
      )}
    </div>
  )
}
